import * as React from "react";
import styled from "styled-components";
import {
  LimitReachedErrorToCheckoutPosition,
  ExpressCheckoutButtonPosition,
} from "constants/Analytics";
import {
  EnhanceSpeechTrack,
  EnhanceSpeechTrackErrorType,
  LimitReachedEnhanceSpeechTrackErrorType,
  getIntervalDescription,
} from "../Utilities";
import { devices, fontFamilies } from "components/shared/Styles";
import { ButtonSecondaryXL } from "../Buttons";
import { BodyLarge } from "components/shared/SpectrumCss";
import { UpsellButton } from "./Upsells";
import { useCurrentUser } from "actions/CurrentUserActions";
import backIconDark from "images/back-icon-dark.svg";
import { webComponentShim } from "components/shared/WebComponentShim";

const LimitReachedContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
  padding: 40px;

  ${devices.medium} {
    padding: 0px;
  }

  ${devices.small} {
    margin-top: 40px;
    text-align: left;
    border-radius: 32px;
    padding: 0px;
  }
`;

const Description = styled(BodyLarge)`
  font: normal normal normal 18px/22px ${fontFamilies};
  line-height: 22px;
  text-align: left;
  color: var(--spectrum-global-color-gray-900);
  ${devices.small} {
    width: 100%;
  }
`;

const BackButton = styled(webComponentShim("sp-action-button")).attrs({
  size: "l",
  quiet: true,
})`
  position: absolute;
  top: 40px;
  left: 48px;
  font-weight: 700;

  ${devices.medium} {
    left: 40px;
  }

  ${devices.small} {
    left: 0px;
    top: 70px;
  }
`;

const LimitReachedHeading = styled.h2`
  font: normal normal 900 32px/40px ${fontFamilies};
  color: var(--spectrum-global-color-static-gray-900);
  margin-bottom: 8px;
  max-width: 400px;
  width: 100%;
  ${devices.small || devices.medium} {
    width: 100%;
  }
`;

export const getUpsellMessageFromError = (
  track: EnhanceSpeechTrack | undefined,
  enhanceTrackErrorType: LimitReachedEnhanceSpeechTrackErrorType
): { heading: string; reason: string; solution?: string } => {
  if (
    enhanceTrackErrorType ===
      EnhanceSpeechTrackErrorType.CUMULATIVE_DURATION_LIMIT &&
    track?.limits?.isLimited
  ) {
    return {
      heading: I18n.t(`enhance_speech.limits.${enhanceTrackErrorType}.heading`),
      reason: I18n.t(`enhance_speech.limits.${enhanceTrackErrorType}.reason`),
      solution: track.limits.retryAtTime
        ? I18n.t(
            `enhance_speech.limits.${enhanceTrackErrorType}.solution.retry`,
            {
              retryInterval: getIntervalDescription(track.limits.retryAtTime),
            }
          )
        : I18n.t(
            `enhance_speech.limits.${enhanceTrackErrorType}.solution.generic`
          ),
    };
  } else if (
    enhanceTrackErrorType === EnhanceSpeechTrackErrorType.VIDEO_NOT_ACCEPTED
  ) {
    return {
      heading: I18n.t(`enhance_speech.limits.${enhanceTrackErrorType}.heading`),
      reason: I18n.t(`enhance_speech.limits.${enhanceTrackErrorType}.reason`),
    };
  } else {
    return {
      heading: I18n.t(`enhance_speech.limits.${enhanceTrackErrorType}.heading`),
      reason: I18n.t(`enhance_speech.limits.${enhanceTrackErrorType}.reason`),
      solution: I18n.t(
        `enhance_speech.limits.${enhanceTrackErrorType}.solution`
      ),
    };
  }
};

export const LimitReached: React.FC<{
  track: EnhanceSpeechTrack | undefined;
  enhanceTrackErrorType: LimitReachedEnhanceSpeechTrackErrorType;
  removeEnhancement: () => Promise<void>;
}> = ({ track, enhanceTrackErrorType, removeEnhancement }) => {
  const { currentUser } = useCurrentUser();

  const upsellMessage = getUpsellMessageFromError(track, enhanceTrackErrorType);

  const position = LimitReachedErrorToCheckoutPosition[enhanceTrackErrorType];

  return (
    <LimitReachedContainer>
      <BackButton quiet onClick={removeEnhancement}>
        <img src={backIconDark} slot="icon" />
        {I18n.t("back")}
      </BackButton>
      <LimitReachedHeading>{upsellMessage.heading}</LimitReachedHeading>
      <Description>{upsellMessage.reason}</Description>
      <UpsellButton
        style={{
          marginTop: 8,
          marginBottom: 16,
          width: "fit-content",
        }}
        position={position}
        enhanceTrackErrorType={enhanceTrackErrorType}
      />
      {upsellMessage.solution && (
        <Description>{upsellMessage.solution}</Description>
      )}
    </LimitReachedContainer>
  );
};
